import React, { useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import {
  setNotificationPermissionModalDismissed,
  type NotificationPermission,
} from '../lib/browserNotifications';
import ModalPortal from './ModalPortal';

type NotificationPermissionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onAllow: () => Promise<void | NotificationPermission>;
  dontShowAgain?: boolean;
};

const NotificationPermissionModal: React.FC<NotificationPermissionModalProps> = ({
  isOpen,
  onClose,
  onAllow,
  dontShowAgain = true,
}) => {
  const { t } = useTranslation();
  const [isRequesting, setIsRequesting] = useState(false);
  const [dontShowChecked, setDontShowChecked] = useState(false);

  if (!isOpen) return null;

  const handleAllow = async () => {
    setIsRequesting(true);
    try {
      await onAllow();
      if (dontShowAgain && dontShowChecked) {
        setNotificationPermissionModalDismissed();
      }
      onClose();
    } catch (err) {
      console.error('Failed to request notification permission:', err);
    } finally {
      setIsRequesting(false);
    }
  };

  const handleNotNow = () => {
    if (dontShowAgain && dontShowChecked) {
      setNotificationPermissionModalDismissed();
    }
    onClose();
  };

  return (
    <ModalPortal>
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="notification-permission-title"
      >
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200 dark:border-gray-700">
          <div className="p-6 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-4">
              <NotificationsActiveIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h2
              id="notification-permission-title"
              className="text-lg font-semibold text-gray-900 dark:text-white mb-2"
            >
              {t('notificationPermission.title')}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
              {t('notificationPermission.description')}
            </p>
          </div>

          <div className="px-6 pb-6 space-y-3">
            <button
              onClick={handleAllow}
              disabled={isRequesting}
              className="w-full py-3 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium transition-colors"
            >
              {isRequesting ? t('notificationPermission.allowing') : t('notificationPermission.allow')}
            </button>
            <button
              onClick={handleNotNow}
              disabled={isRequesting}
              className="w-full py-3 px-4 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors"
            >
              {t('notificationPermission.notNow')}
            </button>
            {dontShowAgain && (
              <label className="flex items-center justify-center gap-2 cursor-pointer text-sm text-gray-500 dark:text-gray-400">
                <input
                  type="checkbox"
                  checked={dontShowChecked}
                  onChange={(e) => setDontShowChecked(e.target.checked)}
                  className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                />
                <span>{t('notificationPermission.dontShowAgain')}</span>
              </label>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default NotificationPermissionModal;
